import React, { useEffect, useState } from 'react';
import { useLanguageStore } from '$/store/languageStore';
import useTeamStore from '@/store/useTeamStore';
import { SHARE_TRY_MINE_KEY } from '@/pages/--roboneo/share/index.page';
import RoleSelectPopup from '$/components/InputPanelToolBar/RoleSelectPopup';
import { useRoleSelectCopy } from '$/components/InputPanelToolBar/useRoleSelectCopy';
import { useHomeIpRoles } from '@/hooks/myIp/useInputIpRoles';
import { useMyIpCreateAction } from '@/hooks/myIp/useMyIpCreateAction';
import HomeInputPanel from './HomeInputPanel';
import useHomeInputPanelComposer from './useHomeInputPanelComposer';

/** 已登录账号/空间切换时清空旧草稿；游客首次登录保留非 IP 输入以便继续发送。 */
export default function HomeInput() {
  const ipRoles = useHomeIpRoles();
  const [session, setSession] = useState({ scopeKey: ipRoles.scopeKey, enabled: ipRoles.enabled, generation: 0 });
  if (session.scopeKey !== ipRoles.scopeKey || session.enabled !== ipRoles.enabled) {
    // render 阶段条件更新让新身份首次渲染就隔离旧 IP；游客初始化/登录不丢失待发送文本。
    setSession({
      scopeKey: ipRoles.scopeKey,
      enabled: ipRoles.enabled,
      generation: session.generation + (session.enabled ? 1 : 0),
    });
  }
  return <HomeInputSession key={session.generation} ipRoles={ipRoles} />;
}

/** 渲染当前身份的输入框，登录提示订阅语言资源，真实 IP 分页列表承接分享内容回填。 */
function HomeInputSession({ ipRoles }: { ipRoles: ReturnType<typeof useHomeIpRoles>; }) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const roleCopy = useRoleSelectCopy();
  const teamOid = useTeamStore((state) => state.teamInfo?.oid);
  const roleCreation = useMyIpCreateAction({ trackingLocation: 'agent_chat' });
  const inputComposer = useHomeInputPanelComposer({
    trackLocation: 'home_page',
    enableSkillSelect: true,
    forceShowModelSelect: true,
    roleList: ipRoles.roles,
  });

  /** 消费分享页或 Agent 广场写入的输入数据，并回填为结构化编辑器内容。 */
  useEffect(() => {
    const raw = sessionStorage.getItem(SHARE_TRY_MINE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(SHARE_TRY_MINE_KEY);
    try {
      inputComposer.actions.setPopupDataContent(JSON.parse(raw));
    } catch (error) {
      console.error('解析分享回填数据失败:', error);
    }
  }, [inputComposer.actions]);

  return (
    <HomeInputPanel
      inputPlaceholder={languageStore.smartcatTranslationText((v) => v.home_page_input_field_text)}
      inputComposer={inputComposer}
      roleCanvasScopeKey={ipRoles.scopeKey}
      createTrackLocation={teamOid ? 'team_add' : 'home_personal_space'}
      showExtendedSelectors
      onRolePopupOpenChange={ipRoles.onRolePopupOpenChange}
      roleSelectContent={(
        <RoleSelectPopup
          trackContext={{ location: 'home_page', roomId: '' }}
          roleList={inputComposer.roleList}
          selectedRoles={inputComposer.selectedRoleInfoList}
          disabled={inputComposer.imageUploadDisabled}
          onSelect={inputComposer.actions.setSelectedRoles}
          scopes={ipRoles.scopes}
          unavailableMessage={ipRoles.enabled ? undefined : roleCopy.homeUnavailable}
          onCreateRole={roleCreation.enabled ? roleCreation.create : undefined}
          creatingRole={roleCreation.pending}
        />
      )}
    />
  );
}
